import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { BriefingProject } from '../../api/briefing';
import { COUNTRY_OPTIONS, LANGUAGE_OPTIONS, getDefaultLanguageForCountry } from '../../constants/geoTargeting';
import ContextHelp from '../../components/ContextHelp/ContextHelp';
import { normalizeTaskUrl, type OnboardingState } from './onboarding';
import styles from './Dashboard.module.css';

interface Props {
  draft: OnboardingState;
  projects: BriefingProject[];
  busy: boolean;
  onChange: (change: Partial<OnboardingState>) => void;
  onSubmit: () => void;
  onClose: () => void;
}

export default function GuidedStart({ draft, projects, busy, onChange, onSubmit, onClose }: Props) {
  const [error, setError] = useState('');
  const firstInput = useRef<HTMLInputElement>(null);
  const briefing = draft.task === 'briefing';
  useEffect(() => { setError(''); firstInput.current?.focus({ preventScroll: true }); }, [draft.task]);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (briefing && !draft.keyword.trim()) { setError('Enter a topic or keyword to research.'); firstInput.current?.focus(); return; }
    if (normalizeTaskUrl(draft.website) === null || (!briefing && !draft.website.trim())) {
      setError('Enter a valid website address, such as example.com/pricing.');
      document.getElementById('guided-website')?.focus(); return;
    }
    setError(''); onSubmit();
  };
  return <form className={styles.guide} onSubmit={submit} aria-labelledby="guide-title" noValidate>
    <div className={styles.sectionHeading}>
      <h2 id="guide-title">{briefing ? 'Let’s prepare your content brief' : 'Let’s set up page monitoring'}</h2>
      <button type="button" className={styles.textButton} onClick={onClose}>Back to choices</button>
    </div>
    <p className={styles.muted}>Share the essentials. Your assistant will help with the next steps.</p>
    <fieldset disabled={busy} className={styles.fields}>
      {briefing && <>
        {projects.length > 0 && <label className={styles.field}>Project
          <select value={draft.projectId} onChange={e => {
            const project = projects.find(p => p.id === e.target.value);
            onChange({ projectId: e.target.value, website: project?.website_url || '' });
          }}><option value="">Let the assistant help me choose</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>}
        <label className={styles.field}>Topic or keyword
          <input ref={firstInput} value={draft.keyword} maxLength={500} onChange={e => onChange({ keyword: e.target.value })} placeholder="e.g. running shoes for beginners" aria-describedby={error ? 'guide-error' : undefined} />
        </label>
        <ContextHelp label="What makes a good keyword?">Use the phrase your audience might search for. For example, “running shoes for beginners” gives the research a clearer focus than “shoes”.</ContextHelp>
      </>}
      <label className={styles.field} htmlFor="guided-website">{briefing ? 'Website (optional)' : 'Page to monitor'}
        <input id="guided-website" ref={briefing ? undefined : firstInput} value={draft.website} maxLength={2048} onChange={e => onChange({ website: e.target.value })} placeholder={briefing ? 'example.com' : 'example.com/pricing'} inputMode="url" autoCapitalize="none" autoCorrect="off" aria-describedby={error ? 'guide-error' : undefined} />
      </label>
      {briefing ? <>
        <ContextHelp label="Why add my website?">Your website gives the assistant context for the brief and helps it reuse the right project. For example, use your shop’s domain for an article about its products.</ContextHelp>
        <div className={styles.formRow}>
          <label className={styles.field}>Target country<select value={draft.country} onChange={e => onChange({ country: e.target.value, language: getDefaultLanguageForCountry(e.target.value) })}>{COUNTRY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></label>
          <label className={styles.field}>Content language<select value={draft.language} onChange={e => onChange({ language: e.target.value })}>{LANGUAGE_OPTIONS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}</select></label>
        </div>
        <ContextHelp label="Which country should I choose?">Choose where your audience searches, which can differ from where you live. For example, select the United States for an article aimed at US customers.</ContextHelp>
      </> : <>
        <label className={styles.field}>Check frequency<select value={draft.frequency} onChange={e => onChange({ frequency: e.target.value as 'daily' | 'weekly' })}><option value="daily">Daily</option><option value="weekly">Weekly</option></select></label>
        <ContextHelp label="How often should I check?">Daily checks suit pages that change frequently; weekly checks suit slower updates. For example, check a competitor’s pricing page daily. Each check can use credits.</ContextHelp>
      </>}
    </fieldset>
    {error && <p id="guide-error" role="alert" className={styles.error}>{error}</p>}
    <div className={styles.guideFooter}>
      <button className={styles.primaryButton} type="submit" disabled={busy}>Prepare with assistant <span aria-hidden="true">→</span></button>
      <Link to={briefing ? '/briefings?create=1' : '/monitoring?create=1'}>Open {briefing ? 'Briefings' : 'Monitoring'} directly</Link>
    </div>
    <p className={styles.small}>Review the assistant’s proposed steps and credit estimate before running a plan.</p>
  </form>;
}
